import { Link } from '../../components/Link';
import { Icon } from '../../components/icons';
import { PublicFooter } from '../../components/PublicFooter';
import { PublicHeader } from '../../components/PublicHeader';
import { Button } from '../../components/ui';
import { Seo, breadcrumbFor, SITE_ORIGIN, ORG_ID } from '../../lib/seo';
import { socialLinkList } from '../../lib/socialLinks';
import { useLegal } from './legal/useLegal';
import { useDisabledPageRedirect } from '../../lib/useDisabledPageRedirect';

/**
 * About page — an E-E-A-T / authority signal (who is behind the product, where it operates, how to
 * reach it) that both buyers and LLMs look for. Deliberately honest: no fabricated team, no home
 * address. The durable facts (founded 2026, operated from Malta, data residency, contact + social)
 * come from real config; the AboutPage JSON-LD references the ONE Organization entity by @id.
 */

const FACTS: Array<{ icon: React.ReactNode; label: string; value: string }> = [
  { icon: <Icon.Activity />, label: 'Founded', value: '2026' },
  { icon: <Icon.ShieldCheck />, label: 'Operated from', value: 'Malta' },
  { icon: <Icon.Layers />, label: 'Data residency', value: 'AWS Frankfurt (EU)' },
  { icon: <Icon.User />, label: 'Ownership', value: 'Independent & self-funded' },
];

const PILLARS: Array<{ title: string; body: string }> = [
  {
    title: 'One coupled action',
    body: 'Every API call runs the full stack — near-duplicate clustering, structural risk scoring, and authoritative mailbox verification — so you get a decision, not a pile of raw signals to stitch together yourself.',
  },
  {
    title: 'Deliverability, not just validity',
    body: 'Beyond "is this address real?", SieveGuard scores the domain (SPF / DKIM / DMARC / MX), checks blacklists, tests inbox placement, and warms sending mailboxes — the whole picture of whether your mail lands.',
  },
  {
    title: 'Free tools, no signup',
    body: 'A suite of unauthenticated developer tools — SPF / DKIM / DMARC checkers, a blacklist checker, an SMTP test and more — because the best way to trust an email-integrity API is to use its building blocks first.',
  },
];

export function AboutPage() {
  const c = useLegal();
  const socials = socialLinkList(c.socialLinks);
  // Super-admin can hide /about. nginx already 404s it for direct/crawler hits; this covers the
  // in-app SPA case (a client-side nav to a hidden page) by redirecting home after mount. Post-mount,
  // not during render — platform flags are seeded into the SSG snapshot, so a render-time redirect
  // would bake a stub into the static HTML and break hydration (same pattern as the feature pages).
  useDisabledPageRedirect(!c.aboutPageEnabled);
  // Render nothing once the LIVE config says the page is off, so it never flashes the About
  // content before the redirect effect fires. Hydration-safe: the first client render still reads
  // the same seeded config the server did (so it matches the prerendered HTML) — this only returns
  // null AFTER the live query flips the flag off post-mount, which is a normal update, not a
  // hydration mismatch. In prod a direct hit never even reaches here (the nginx gate 404s it).
  if (!c.aboutPageEnabled) return null;

  return (
    <>
      <Seo
        title="About"
        description="An email-integrity API that stops bot and duplicate signups, scores risk, and verifies deliverability. Independent, built in Malta, founded 2026."
        path="/about"
        jsonLd={[
          breadcrumbFor([
            { name: 'Home', path: '/' },
            { name: 'About', path: '/about' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About SieveGuard',
            url: `${SITE_ORIGIN}/about/`,
            // Point at the ONE Organization entity (defined site-wide by baseGraph) rather than
            // minting a second company node.
            mainEntity: { '@id': ORG_ID },
          },
        ]}
      />
      <div className="mx-auto max-w-[1210px] px-6 pb-20">
        <PublicHeader />

        <section className="mx-auto max-w-3xl py-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-accentdeep/30 bg-accentdeep/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" /> About SieveGuard
          </span>
          <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.05] tracking-[-0.02em] md:text-6xl">
            Email integrity, <span className="text-accent">as one call.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-muted">
            SieveGuard is an email-integrity API for product, growth, and deliverability teams. It
            clusters near-duplicate email addresses as they arrive, scores bot and multi-accounting
            risk, and verifies deliverability — catching fake and duplicate signups before they cost
            you money, skew your metrics, or drag down your sending reputation.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register">
              <Button variant="primary" className="h-11 px-5">
                Start free
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="ghost" className="h-11 px-5">
                See pricing
              </Button>
            </Link>
            <Link to="/docs">
              <Button variant="ghost" className="h-11 px-5">
                Read the docs
              </Button>
            </Link>
          </div>
        </section>

        <section className="mx-auto grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-4">
          {FACTS.map((f) => (
            <div key={f.label} className="rounded-xl border border-bordersoft bg-panel p-4 text-center">
              <div className="mx-auto flex h-8 w-8 items-center justify-center text-accent">{f.icon}</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">{f.label}</div>
              <div className="mt-0.5 text-[13.5px] font-semibold text-txt">{f.value}</div>
            </div>
          ))}
        </section>

        <section className="mx-auto mt-20 max-w-3xl">
          <h2 className="font-display text-3xl font-bold tracking-tight">Why SieveGuard exists</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-muted">
            Signup fraud is quiet. Bot-enumerated and multi-accounting registrations don&rsquo;t
            announce themselves — they inflate your user counts, burn free-tier resources and email
            credits, and quietly poison your lists until deliverability craters. Most teams only
            notice once the damage is done. SieveGuard was built to catch it at the front door: at
            the moment of signup, in one call, with a clear verdict you can act on.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-xl border border-bordersoft bg-panel p-5">
                <h3 className="text-[15px] font-semibold text-txt">{p.title}</h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-20 max-w-3xl rounded-2xl border border-bordersoft bg-panel p-8">
          <h2 className="font-display text-2xl font-bold tracking-tight">Who&rsquo;s behind it</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-muted">
            SieveGuard is an independent, self-funded business operated from {c.governingLawJurisdiction},
            with customer data held in {c.dataResidencyRegion}. No investors, no growth-at-all-costs
            mandate — just a focused product and a direct line to the person building it.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px]">
            <a className="text-accent hover:underline" href={`mailto:${c.supportEmail}`}>
              {c.supportEmail}
            </a>
            <Link to="/contact" className="text-accent hover:underline">
              Contact &amp; legal identity →
            </Link>
            {socials.length > 0 && (
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted">
                {socials.map((s) => (
                  <a key={s.key} href={s.url} target="_blank" rel="me noopener noreferrer" className="hover:text-accent">
                    {s.label}
                  </a>
                ))}
              </span>
            )}
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default AboutPage;
